<template>
  <!-- a cancelled line is faded cell by cell, not as a whole row: the button to delete it stays bright -->
  <tr class="border-b border-slate-200 align-top dark:border-slate-500">
    <td
      class="px-3 py-2 text-center"
      :class="{ 'opacity-60': item.is_cancelled }"
    >
      {{ item.quantity ?? 1 }}
    </td>
    <td class="px-3 py-2" :class="{ 'opacity-60': item.is_cancelled }">
      <!-- a free line: no invoice behind it -->
      <div v-if="item.is_free" class="font-semibold">{{ item.name }}</div>
      <!-- a correction line (credit note): the documents it is made of -->
      <template v-else-if="item.rectification">
        <div
          v-for="document in correctionDocuments"
          :key="document.id"
          class="mb-1"
        >
          <component
            :is="canSeeInvoice ? 'RouterLink' : 'span'"
            class="font-semibold"
            :class="{ 'text-primary hover:underline': canSeeInvoice }"
            v-bind="invoiceLink(document.link_id)"
          >
            {{ document.reference }}
            {{ $t('global.of_date') }}
            {{ formatDate(document.emission_date) }}
          </component>
          <div
            v-if="
              document.id ===
              (item.rectification.invoice_id ?? correctionDocuments[0].id)
            "
            class="my-1 w-fit rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-200"
          >
            {{ $t('global.rectification') }}
            <template v-if="item.rectification.reason">
              —
              {{
                $t(
                  `enums.activityLog.commission_reason.${item.rectification.reason}`
                )
              }}
            </template>
          </div>
          <div v-if="document.company" class="text-xs text-gray-500">
            {{ document.company }}
            <template v-if="document.responsable">
              — {{ document.responsable }}
            </template>
          </div>
          <div v-if="document.deceased" class="text-xs text-gray-500">
            {{ $t('global.deceased') }} : {{ document.deceased }}
          </div>
        </div>
      </template>
      <!-- a calculated line: the commission on an invoice -->
      <div v-else>
        <component
          :is="canSeeInvoice && item.source_invoice_id ? 'RouterLink' : 'span'"
          class="font-semibold"
          :class="{
            'text-primary hover:underline':
              canSeeInvoice && item.source_invoice_id
          }"
          v-bind="invoiceLink(item.source_invoice_id)"
        >
          {{ $t('global.commission_on_document') }}
          {{ item.document_reference }}
          {{ $t('global.of_date') }}
          {{ formatDate(item.document_date) }}
        </component>
        <div v-if="documentInfo.company" class="text-xs text-gray-500">
          {{ documentInfo.company }}
          <template v-if="documentInfo.responsible">
            — {{ documentInfo.responsible }}
          </template>
        </div>
        <div v-if="documentInfo.deceased" class="text-xs text-gray-500">
          {{ $t('global.deceased') }} : {{ documentInfo.deceased }}
        </div>
      </div>

      <p v-if="item.description" class="mt-1 text-sm whitespace-pre-line">
        {{ item.description }}
      </p>
      <p
        v-if="item.internal_note"
        class="mt-1 text-xs whitespace-pre-line text-red-500"
      >
        {{ item.internal_note }}
      </p>

      <div
        v-if="globalBasis"
        class="flex max-w-xl justify-between gap-4 text-xs text-gray-500"
        data-test="calculation-base"
      >
        <span>
          {{ globalBasis.label }}
          <template v-if="item.pricing_basis">
            ({{ item.pricing_basis }})
          </template>
          — {{ globalBasis.rate }} %
        </span>
        <AmountFormatter
          class="shrink-0"
          :class="amountClass(item.base_amount)"
          :amount="item.base_amount"
        />
      </div>

      <div
        v-for="(product, index) in products"
        :key="index"
        class="flex max-w-xl justify-between gap-4 text-xs text-gray-500"
      >
        <span>
          <RouterLink
            v-if="canSeeItemSettings && product.item_id"
            class="text-primary hover:underline"
            :to="{
              name: 'settings_edit_item_route',
              params: { id: product.item_id }
            }"
          >
            {{ product.name }}
          </RouterLink>
          <template v-else>{{ product.name }}</template>
          — {{ product.rate }}
          {{ product.rate_type === 'pc' ? '%' : '€' }}
        </span>
        <AmountFormatter
          class="shrink-0"
          :class="amountClass(product.commission_htva)"
          :amount="product.commission_htva"
        />
      </div>

      <div
        v-if="item.is_cancelled || flatCommission"
        class="mt-2 flex flex-row flex-wrap items-center gap-1"
      >
        <span
          v-if="item.is_cancelled"
          class="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"
        >
          {{ $t('global.cancelled') }}
        </span>
        <template v-if="flatCommission">
          <span
            class="rounded bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700 capitalize-first"
            data-test="flat-commission"
          >
            {{ $t('global.commission_flat') }}
          </span>
          <button
            v-if="editable"
            type="button"
            class="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            @click="$emit('restore', item)"
          >
            <FontAwesomeIcon :icon="['fal', 'undo']" />
            {{ $t('button.restore_automatic_amount') }}
          </button>
        </template>
      </div>
    </td>
    <td
      class="px-3 py-2 text-center"
      :class="{ 'opacity-60': item.is_cancelled }"
    >
      {{ item.vat_rate }} %
    </td>
    <td
      class="px-3 py-2 text-center"
      :class="{
        'line-through opacity-60': item.is_cancelled
      }"
    >
      <AmountFormatter :class="amountClass(unitPrice)" :amount="unitPrice" />
    </td>
    <td
      class="px-3 py-2 text-center"
      :class="{
        'line-through opacity-60': item.is_cancelled
      }"
    >
      <AmountFormatter
        :class="amountClass(withVat(unitPrice, item.vat_rate))"
        :amount="withVat(unitPrice, item.vat_rate)"
      />
    </td>
    <td
      class="px-3 py-2 text-center"
      :class="{
        'line-through opacity-60': item.is_cancelled
      }"
    >
      <AmountFormatter
        :class="amountClass(withVat(item.commission_htva, item.vat_rate))"
        :amount="withVat(item.commission_htva, item.vat_rate)"
      />
    </td>
    <td v-if="editable" class="px-3 py-2">
      <div
        v-if="canDeleteItem"
        class="flex flex-row justify-end gap-1 align-top"
      >
        <!-- a cancelled line can only be deleted: there is nothing to move or to change in it -->
        <template v-if="canEditItem">
          <div
            class="handle flex h-6 w-6 cursor-move items-center justify-center rounded bg-gradient-to-br from-gray-200 to-gray-300 p-2"
            role="img"
            :aria-label="$t('button.move')"
            :title="$t('button.move')"
          >
            <FontAwesomeIcon :icon="['fal', 'arrows-from-line']" fixed-width />
          </div>
          <button
            type="button"
            class="flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-gradient-to-br from-yellow-400 to-yellow-500 p-2 text-slate-800"
            :title="$t('button.edit')"
            @click="$emit('edit', item)"
          >
            <FontAwesomeIcon :icon="['fal', 'pen']" fixed-width />
          </button>
        </template>
        <button
          type="button"
          class="flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-gradient-to-br from-red-600 to-red-700 p-2 text-white"
          :title="$t('button.delete')"
          @click="$emit('delete', item)"
        >
          <FontAwesomeIcon :icon="['fal', 'trash']" fixed-width />
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import { mapGetters } from 'vuex'
import AmountFormatter from '@c/AmountFormatter.vue'
import {
  amountClass,
  isDeletableItem,
  isEditableItem,
  withVat
} from '@/views/invoicing/show/commissionNote/components/commissionNoteItem'

/** One line of a commission note: free line, commission on an invoice, or correction. */
export default {
  name: 'CommissionNoteItemRow',
  components: { AmountFormatter },
  props: {
    item: { type: Object, required: true },
    /** The note is open and the user can change its lines */
    editable: { type: Boolean, default: false },
    canSeeInvoice: { type: Boolean, default: false },
    canSeeItemSettings: { type: Boolean, default: false }
  },
  emits: ['edit', 'delete', 'restore'],
  computed: {
    ...mapGetters({ initialMetadata: 'metadata/getInitialMetadata' }),
    // a total or a without third-party costs calculation has no detail per product: it shows its base and its rate
    globalBasis() {
      const calculation = this.item.details?.calculation
      const basis = calculation?.basis

      if (
        this.flatCommission ||
        this.item.is_free ||
        !basis ||
        basis === 'per_product'
      ) {
        return null
      }

      const options = this.initialMetadata?.enums?.commission_calculation_basis

      return {
        label:
          (options ?? []).find((option) => option.id === basis)?.name ?? basis,
        rate: calculation.rate_percent ?? 0
      }
    },
    canEditItem() {
      return isEditableItem(this.item)
    },
    canDeleteItem() {
      return isDeletableItem(this.item)
    },
    amountEdited() {
      return !!this.item.amount_edited && !this.item.is_free
    },
    // an amount typed by hand: only the flat commission is shown here, its calculation is in the edit form
    flatCommission() {
      return this.item.is_flat_commission ?? this.amountEdited
    },
    // the documents of a correction line; none when they were released (the note was cancelled), then what the line itself kept
    correctionDocuments() {
      if (this.item.documents?.length) {
        return this.item.documents.map((document) => ({
          ...document,
          link_id: document.id
        }))
      }

      if (!this.item.document_reference) {
        return []
      }

      return [
        {
          id: this.item.rectification?.invoice_id ?? this.item.id,
          link_id: this.item.rectification?.invoice_id ?? null,
          reference: this.item.document_reference,
          emission_date: this.item.document_date,
          company: this.item.customer_company_name,
          responsable: this.item.responsible_name,
          deceased: this.item.deceased_name
        }
      ]
    },
    // what the invoice says, from the facts of the line or, failing that, from its document
    documentInfo() {
      const document = this.item.documents?.[0]

      return {
        company: this.item.customer_company_name ?? document?.company,
        responsible: this.item.responsible_name ?? document?.responsable,
        deceased: this.item.deceased_name ?? document?.deceased
      }
    },
    // the detail per product, or the former detail by line
    products() {
      if (this.flatCommission) {
        return []
      }

      return this.item.products?.length
        ? this.item.products
        : (this.item.details?.lines ?? [])
    },
    // a free line has its own unit price, a calculated one is a single amount
    unitPrice() {
      return this.item.is_free
        ? this.item.unit_price_htva
        : this.item.commission_htva
    }
  },
  methods: {
    amountClass,
    withVat,
    formatDate(date) {
      return date ? this.$h.formatDate(date, 'D MMMM YYYY') : ''
    },
    invoiceLink(invoiceId) {
      return this.canSeeInvoice && invoiceId
        ? {
            to: {
              name: 'invoice_show_route',
              params: { invoicing_id: invoiceId }
            }
          }
        : {}
    }
  }
}
</script>
